import {
    appendWidgetGeoToPayload,
    fetchGeoJsGeo,
    postEnrollmentProgress,
    validateWidgetCoupon,
    type WidgetEnrollmentProgressStage,
} from './widgetApi';
import { getMockOffer, getMockTheme, MOCK_CIDADES } from './widgetMockData';
import type { WidgetTheme } from './widgetStyles';
import { getThemeVarsCss, WIDGET_STYLES } from './widgetStyles';
import {
    buildPlansByCustomerAndMethod,
    calcParcelamento,
    computeScholarshipDiscount,
    enrollmentConfirmationNoteHtml,
    escapeHtmlAttr,
    formatIsoDateTimeForDisplay,
    formatPrice,
    getPlanNetPrice,
    isValidCpf,
    maskCep,
    maskCpf,
    maskTelefone,
    parsePrice,
    STEPS,
    STEP_NAMES,
    UFS,
    type PaymentPlanRow,
} from './widgetHelpers';

export type { DecolaWidgetInitOptions } from './widgetTypes';

type FormDataState = Record<string, unknown>;

function validateDadosPessoais(formData: FormDataState): Record<string, string> {
    const err: Record<string, string> = {};
    if (formData.estrangeiro) {
        const passaporte = String(formData.passaporte || '').trim();
        if (!passaporte) err.passaporte = 'Passaporte é obrigatório.';
        const fp = String(formData.formaPagamento || '');
        if (fp === 'boleto') {
            err.pagamento_estrangeiro =
                'Esse meio de pagamento não é permitido para estrangeiros. Volte ao passo «Forma de pagamento» e escolha cartão de crédito, ou desmarque «é estrangeiro?» se possuir CPF brasileiro.';
        } else if (fp === 'pix') {
            err.pagamento_estrangeiro =
                'No Brasil, o PIX exige CPF do pagador. Volte ao passo «Forma de pagamento» e escolha cartão de crédito, ou desmarque «é estrangeiro?» se possuir CPF brasileiro.';
        }
    } else {
        const cpfDigits = String(formData.cpf || '').replace(/\D/g, '');
        if (cpfDigits.length !== 11) err.cpf = 'CPF inválido. Informe os 11 dígitos.';
        else if (!isValidCpf(String(formData.cpf))) err.cpf = 'CPF inválido.';
    }
    const nome = String(formData.nome || '').trim();
    if (!nome) {
        err.nome = 'Nome é obrigatório.';
    } else {
        if (!/^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\s]+$/.test(nome)) {
            err.nome = 'Nome deve conter apenas letras (sem números ou caracteres especiais).';
        } else {
            const partes = nome.split(/\s+/).filter((p) => p.length > 0);
            if (partes.length < 2) err.nome = 'Informe nome e sobrenome.';
        }
    }
    const email = String(formData.email || '').trim();
    if (!email) err.email = 'E-mail é obrigatório.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = 'Informe um e-mail válido.';
    const telDigits = String(formData.telefone || '').replace(/\D/g, '');
    if (telDigits.length === 0) err.telefone = 'Telefone é obrigatório.';
    else if (telDigits.length < 10 || telDigits.length > 11) {
        err.telefone = 'Telefone inválido. Informe DDD + número (10 ou 11 dígitos).';
    }
    return err;
}

function validateEndereco(formData: FormDataState): Record<string, string> {
    const err: Record<string, string> = {};
    const cepDigits = String(formData.cep || '').replace(/\D/g, '');
    if (cepDigits.length !== 8) err.cep = 'CEP inválido. Informe os 8 dígitos.';
    const endereco = String(formData.endereco || '').trim();
    if (!endereco) err.endereco = 'Endereço é obrigatório.';
    else if (endereco.length > 100) err.endereco = 'Endereço deve ter no máximo 100 caracteres.';
    const numero = String(formData.numero || '').trim();
    if (!numero) err.numero = 'Número é obrigatório.';
    else if (numero.length > 8) err.numero = 'Número deve ter no máximo 8 caracteres.';
    const complemento = String(formData.complemento || '').trim();
    if (complemento.length > 60) err.complemento = 'Complemento deve ter no máximo 60 caracteres.';
    const bairro = String(formData.bairro || '').trim();
    if (!bairro) err.bairro = 'Bairro é obrigatório.';
    else if (bairro.length > 80) err.bairro = 'Bairro deve ter no máximo 80 caracteres.';
    if (!String(formData.estado || '').trim()) err.estado = 'Selecione o estado.';
    if (!String(formData.cidade || '').trim()) err.cidade = 'Selecione a cidade.';
    return err;
}

export function createMockWidget(container: string | HTMLElement, options: DecolaWidgetInitOptions): void {
    const root = typeof container === 'string' ? document.querySelector(container) : container;
    if (!root) {
        console.error('[DecolaWidget] Container não encontrado:', container);
        return;
    }

    const offerId = options.offerId || 'mock';
    const themeId = options.themeId || '1';
    const sellerId = options.sellerId || '';
    const campaignId = options.campaignId || '';

    const offer = getMockOffer(offerId);
    const theme: WidgetTheme | null = getMockTheme(themeId);
    const themeCode = theme?.code != null ? theme.code : null;

    const plansByCustomerAndMethod = buildPlansByCustomerAndMethod(offer.paymentPlans || []);
    const customerTypes = Object.keys(plansByCustomerAndMethod);
    const hasPublico = customerTypes.includes('publico');
    const hasEgresso = customerTypes.includes('egresso');

    const apiBase = String(options.apiBaseUrl ?? '').replace(/\/$/, '');
    const widgetSessionId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `mock_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const numericOfferId = ((): number => {
        const s = String(offerId || '').trim();
        if (!s) return 1;
        const semi = s.indexOf(';');
        if (semi > 0) {
            const n = parseInt(s.slice(0, semi), 10);
            if (!Number.isNaN(n)) return n;
        }
        const n = parseInt(s, 10);
        return Number.isNaN(n) ? 1 : n;
    })();

    const firstStep = STEPS.PAGAMENTO;
    let currentStep = firstStep;
    let cidadesList: { id: number; nome: string }[] = [];
    let cepLoading = false;
    let termosError = false;
    let pagamentoError = false;
    let dadosPessoaisErrors: Record<string, string> = {};
    let enderecoErrors: Record<string, string> = {};
    let checkoutUrl: string | null = null;
    let inlinePaymentResult: Record<string, unknown> | null = null;
    let checkoutErrorMessage: string | null = null;
    let cardTokenizeErrorMessage: string | null = null;
    let submitting = false;
    let couponError: string | null = null;
    let couponApplying = false;

    const mockAcceptanceLabel = 'Li e aceito o termo de inscrição (demonstração)';
    const mockAcceptancePdfUrl: string | null = null;

    const formData: FormDataState = {
        offerId,
        themeId,
        sellerId,
        campaignId,
        paymentPlanId: null,
        tipoIngresso: !hasPublico && hasEgresso ? 'egresso' : 'publico',
        formaPagamento: 'pix',
        parcelas: 1,
        cupom: false,
        codigoCupom: '',
        coupon_id: null as number | null,
        coupon_discount_type: null as string | null,
        coupon_amount: null as number | null,
        coupon_code_display: '',
        coupon_scholarship_name: '',
        aceiteTermo: false,
        possuiNomeSocial: false,
        estrangeiro: false,
        themeCode,
        theme: themeCode,
        geo_ip: null,
        latitude: null,
        longitude: null,
        geo_continent: null,
        geo_country: null,
        geo_region: null,
        geo_city: null,
    };

    const firstCustomerType = String(formData.tipoIngresso);
    const methodsForFirst = plansByCustomerAndMethod[firstCustomerType] || {};
    const firstMethod = ['pix', 'cartao', 'boleto'].find((m) => methodsForFirst[m]) || Object.keys(methodsForFirst)[0] || 'pix';
    formData.formaPagamento = firstMethod;

    void fetchGeoJsGeo().then((geo) => {
        if (!geo) return;
        Object.assign(formData, geo);
    });

    const shadow = root.attachShadow({ mode: 'open' });

    function getCurrentPlan(): PaymentPlanRow | null {
        const ct = String(formData.tipoIngresso);
        const pm = String(formData.formaPagamento);
        return (plansByCustomerAndMethod[ct] || {})[pm] || null;
    }

    function installmentsForSelectedPlanMock(): number {
        const plan = getCurrentPlan();
        if (plan) {
            return Math.max(1, parseInt(String(plan.installments), 10) || 1);
        }
        return Math.max(1, parseInt(String(formData.parcelas), 10) || 1);
    }

    function syncPlanFromFormData(): void {
        const plan = getCurrentPlan();
        formData.paymentPlanId = plan?.id || null;
        const maxP = plan ? parseInt(String(plan.installments), 10) || 1 : 1;
        formData.parcelas = maxP;
    }

    syncPlanFromFormData();

    async function syncProgress(stage: WidgetEnrollmentProgressStage): Promise<boolean> {
        if (!apiBase) {
            console.warn('[DecolaWidget mock] Sem apiBaseUrl; progresso não enviado.');
            return true;
        }
        collectStepData();
        syncPlanFromFormData();
        const payload: Record<string, unknown> = {
            session_id: widgetSessionId,
            offer_id: numericOfferId,
            stage,
        };
        if (stage === 'dados_basicos') {
            payload.name = String(formData.nome || '').trim();
            payload.email = String(formData.email || '').trim();
            payload.social_name = formData.possuiNomeSocial ? String(formData.nomeSocial || '').trim() || null : null;
            payload.cpf = formData.estrangeiro ? null : String(formData.cpf || '').trim() || null;
            payload.passport = formData.estrangeiro ? String(formData.passaporte || '').trim() || null : null;
            payload.phone = String(formData.telefone || '').trim() || null;
        }
        if (stage === 'dados_endereco') {
            payload.postal_code = String(formData.cep || '').replace(/\D/g, '') || null;
            payload.country_id = null;
            payload.state_id = null;
            payload.city_id = null;
            payload.neighborhood = String(formData.bairro || '').trim() || null;
            payload.street = String(formData.endereco || '').trim() || null;
            payload.street_number = String(formData.numero || '').trim() || null;
            payload.complement = String(formData.complemento || '').trim() || null;
        }
        if (stage === 'pagamento') {
            syncPlanFromFormData();
            const plan = getCurrentPlan();
            if (plan && (plan as { paymentPlanDbId?: number }).paymentPlanDbId != null) {
                payload.payment_plan_id = (plan as { paymentPlanDbId: number }).paymentPlanDbId;
                payload.admission_type_id = (plan as { admissionTypeDbId: number }).admissionTypeDbId;
            }
            if (formData.coupon_id != null && Number(formData.coupon_id) > 0) {
                payload.coupon_id = Number(formData.coupon_id);
            }
        }
        appendWidgetGeoToPayload(payload, formData);
        try {
            await postEnrollmentProgress(apiBase, payload as Parameters<typeof postEnrollmentProgress>[1]);
            return true;
        } catch (e) {
            checkoutErrorMessage = e instanceof Error ? e.message : 'Erro ao sincronizar inscrição.';
            render();
            return false;
        }
    }

    function collectStepData(): void {
        shadow.querySelectorAll('input').forEach((input) => {
            const el = input as HTMLInputElement;
            if (el.type === 'checkbox') {
                if (el.id) formData[el.id] = el.checked;
            } else if (el.id) formData[el.id] = el.value || '';
        });
        shadow.querySelectorAll('select').forEach((sel) => {
            const el = sel as HTMLSelectElement;
            if (el.id) formData[el.id] = el.value || '';
        });
    }

    async function applyCouponFlow(): Promise<void> {
        collectStepData();
        couponError = null;
        const code = String(formData.codigoCupom || '').trim();
        if (!code) {
            couponError = 'Informe o código do cupom.';
            render();
            return;
        }
        couponApplying = true;
        render();
        try {
            if (apiBase) {
                const res = await validateWidgetCoupon(apiBase, { offer_id: numericOfferId, code });
                formData.coupon_id = res.coupon_id;
                formData.coupon_discount_type = String(res.discount_type);
                formData.coupon_amount = parsePrice(res.amount);
                formData.coupon_code_display = res.code;
                formData.coupon_scholarship_name = res.scholarship_name || '';
            } else {
                if (code.toUpperCase() === 'DEMO10') {
                    formData.coupon_id = null;
                    formData.coupon_discount_type = 'P';
                    formData.coupon_amount = 10;
                    formData.coupon_code_display = 'DEMO10';
                    formData.coupon_scholarship_name = 'Demonstração';
                } else {
                    throw new Error('Cupom inválido. No modo demonstração, use o código DEMO10.');
                }
            }
            couponError = null;
        } catch (e) {
            formData.coupon_id = null;
            formData.coupon_discount_type = null;
            formData.coupon_amount = null;
            formData.coupon_code_display = '';
            formData.coupon_scholarship_name = '';
            couponError = e instanceof Error ? e.message : 'Cupom inválido.';
        } finally {
            couponApplying = false;
            render();
        }
    }

    function renderPagamentoStep(): string {
        const ct = String(formData.tipoIngresso);
        const methods = plansByCustomerAndMethod[ct] || {};
        const plan = getCurrentPlan();
        const planNet = plan ? getPlanNetPrice(plan) : 0;
        const originalPrice = plan ? parsePrice(plan.price ?? 0) : 0;
        const discountVal = plan ? parsePrice(plan.discount ?? 0) : 0;
        const couponDisc =
            plan && formData.coupon_discount_type != null && formData.coupon_amount != null
                ? computeScholarshipDiscount(
                      planNet,
                      String(formData.coupon_discount_type),
                      Number(formData.coupon_amount),
                  )
                : 0;
        const finalTotal = Math.max(0, planNet - couponDisc);
        const price = plan ? formatPrice(planNet) : '—';
        let priceHtml = price;
        if (plan && couponDisc > 0) {
            priceHtml = `<span class="valor-original">De ${formatPrice(planNet)}</span> por ${formatPrice(finalTotal)}`;
        } else if (plan && discountVal > 0) {
            priceHtml = `<span class="valor-original">De ${formatPrice(originalPrice)}</span> por ${formatPrice(planNet)}`;
        }
        const descontoLinhas: string[] = [];
        if (plan && discountVal > 0) {
            descontoLinhas.push(`<div class="desconto">Desconto do plano: ${formatPrice(discountVal)}</div>`);
        }
        if (plan && couponDisc > 0) {
            const nomeCupom = String(formData.coupon_scholarship_name || formData.coupon_code_display || '').trim();
            descontoLinhas.push(
                `<div class="desconto">Cupom${nomeCupom ? ` (${escapeHtmlAttr(nomeCupom)})` : ''}: ${formatPrice(couponDisc)}</div>`,
            );
        }
        const descontoHtml = descontoLinhas.join('');
        const maxParcelas = parseInt(String(plan?.installments), 10) || 1;
        const { valorExibicao } =
            plan && maxParcelas > 1 ? calcParcelamento(finalTotal, maxParcelas) : { valorExibicao: finalTotal };

        let resumoTexto = '';
        if (plan) {
            if (maxParcelas > 1) {
                resumoTexto = `em ${maxParcelas}x de ${formatPrice(valorExibicao)}`;
            } else if (formData.formaPagamento === 'pix') {
                resumoTexto = 'À vista no PIX';
            } else if (formData.formaPagamento === 'cartao') {
                resumoTexto = 'À vista no cartão';
            } else {
                resumoTexto = 'À vista no boleto';
            }
        }

        const showTipoIngresso = customerTypes.length > 0;
        const paymentLabels: Record<string, string> = { pix: 'PIX', cartao: 'Cartão de crédito', boleto: 'Boleto' };
        const paymentOptions = ['pix', 'cartao', 'boleto']
            .filter((m) => methods[m])
            .map((m) => {
                const p = methods[m];
                const installmentsNum = parseInt(String(p?.installments), 10) || 1;
                const small = installmentsNum > 1 ? `Em ${installmentsNum}x` : 'À vista';
                const description = String(p?.description || '').trim();
                return { key: m, label: paymentLabels[m] || m, small, description };
            });

        let tipoIngressoHtml = '';
        if (showTipoIngresso) {
            tipoIngressoHtml = `
        <div class="section-label">Tipo de ingresso</div>
        <div class="btn-group">
          <button type="button" class="btn-tipo ${ct === 'publico' ? 'selected' : ''}" data-tipo="publico">Público Geral</button>
          <button type="button" class="btn-tipo ${ct === 'egresso' ? 'selected' : ''}" data-tipo="egresso">Egresso</button>
        </div>
      `;
        }

        let parcelasHtml = '';
        if (maxParcelas > 1 && plan) {
            const vp = calcParcelamento(finalTotal, maxParcelas).valorExibicao;
            parcelasHtml = `
        <div class="field field-parcelamento">
          <label>Parcelamento</label>
          <p class="parcelamento-valor">Em ${maxParcelas}x de ${formatPrice(vp)}</p>
        </div>
      `;
        }

        return `
      ${tipoIngressoHtml}
      <div class="section-label">Forma de pagamento</div>
      ${paymentOptions.length === 0 ? '<p class="field-error">Nenhum plano de pagamento disponível.</p>' : ''}
      ${paymentOptions
          .map(
              (opt) => `
        <div class="payment-option ${formData.formaPagamento === opt.key ? 'selected' : ''}" data-pag="${opt.key}">
          <input type="radio" name="pag" value="${opt.key}" ${formData.formaPagamento === opt.key ? 'checked' : ''}>
          <div class="text">${opt.label}${opt.description ? ` <span class="payment-desc">${escapeHtmlAttr(opt.description)}</span>` : ''}${opt.small ? `<small>${opt.small}</small>` : ''}</div>
        </div>
      `,
          )
          .join('')}
      ${parcelasHtml}
      <div class="field">
        <label class="check"><input type="checkbox" id="cupom" ${formData.cupom ? 'checked' : ''}> Possui cupom de desconto?</label>
      </div>
      ${
          formData.cupom
              ? `<div class="field">
        <label>Código do cupom</label>
        <div class="cupom-row">
          <input type="text" id="codigoCupom" name="codigoCupom" autocomplete="off" placeholder="Digite o código" value="${escapeHtmlAttr(String(formData.codigoCupom || ''))}" maxlength="64">
          <button type="button" class="btn-cupom-aplicar" id="btn-aplicar-cupom" ${couponApplying ? 'disabled' : ''}>${couponApplying ? '…' : 'Aplicar'}</button>
        </div>
        ${couponError ? `<div class="cupom-error">${escapeHtmlAttr(couponError)}</div>` : ''}
        ${!apiBase ? `<div class="cupom-error" style="color:rgba(255,255,255,0.85)">Modo demo: use o código <strong>DEMO10</strong>.</div>` : ''}
      </div>`
              : ''
      }
      <div class="resumo">
        <div class="valor">${priceHtml}</div>
        ${descontoHtml}
        <div class="obs">${resumoTexto}</div>
      </div>
      <div class="termos">
        <label>
          <input type="checkbox" id="aceiteTermo" ${formData.aceiteTermo ? 'checked' : ''}>
          <span>${escapeHtmlAttr(mockAcceptanceLabel)}${
              mockAcceptancePdfUrl
                  ? ` <a class="termos-pdf-link" href="${escapeHtmlAttr(mockAcceptancePdfUrl)}" target="_blank" rel="noopener noreferrer">Ver termo (PDF)</a>`
                  : ''
          }</span>
        </label>
        ${pagamentoError ? '<div class="termos-error">Nenhum plano de pagamento disponível para esta combinação.</div>' : ''}
        ${termosError ? '<div class="termos-error">É obrigatório aceitar o termo para continuar.</div>' : ''}
      </div>
    `;
    }

    function renderDadosPessoaisStep(): string {
        const est = formData.estrangeiro ? true : false;
        return `
      ${
          dadosPessoaisErrors.pagamento_estrangeiro
              ? `<div class="field-error" style="margin-bottom:0.75rem;line-height:1.45;">${escapeHtmlAttr(dadosPessoaisErrors.pagamento_estrangeiro)}</div>`
              : ''
      }
      <div class="field">
        <label>${est ? 'Passaporte' : 'CPF'}</label>
        <div class="field-row">
          <input type="text" id="${est ? 'passaporte' : 'cpf'}" placeholder="${est ? 'Número do passaporte' : '000.000.000-00'}" value="${escapeHtmlAttr(est ? String(formData.passaporte || '') : String(formData.cpf || ''))}" ${!est ? 'maxlength="14"' : ''}>
          <label class="check"><input type="checkbox" id="estrangeiro" ${est ? 'checked' : ''}> é estrangeiro?</label>
        </div>
        <div id="cpf-error" class="field-error" style="${dadosPessoaisErrors.cpf ? '' : 'display:none'}">${dadosPessoaisErrors.cpf || ''}</div>
        ${dadosPessoaisErrors.passaporte ? `<div class="field-error">${dadosPessoaisErrors.passaporte}</div>` : ''}
      </div>
      <div class="field">
        <label>Nome</label>
        <input type="text" id="nome" placeholder="Digite seu nome" value="${escapeHtmlAttr(String(formData.nome || ''))}" maxlength="100">
        ${dadosPessoaisErrors.nome ? `<div class="field-error">${dadosPessoaisErrors.nome}</div>` : ''}
      </div>
      <div class="field">
        <label class="check"><input type="checkbox" id="possuiNomeSocial" ${formData.possuiNomeSocial ? 'checked' : ''}> Possuo nome social<span class="hint-icon" title="Nome social se refere à designação pela qual a pessoa travesti ou transexual se identifica e é socialmente reconhecida. Não tendo relação com identidade de gênero ou sexo atribuído no nascimento.">i</span></label>
      </div>
      ${formData.possuiNomeSocial ? `
      <div class="field">
        <label>Nome Social</label>
        <input type="text" id="nomeSocial" placeholder="Digite seu nome social" value="${escapeHtmlAttr(String(formData.nomeSocial || ''))}" maxlength="100">
      </div>
      ` : ''}
      <div class="field">
        <label>E-mail</label>
        <input type="email" id="email" placeholder="exemplo@exemplo.com" value="${escapeHtmlAttr(String(formData.email || ''))}" maxlength="250">
        ${dadosPessoaisErrors.email ? `<div class="field-error">${dadosPessoaisErrors.email}</div>` : ''}
      </div>
      <div class="field">
        <label>Telefone</label>
        <input type="tel" id="telefone" placeholder="(51) 99999-9999" value="${escapeHtmlAttr(String(formData.telefone || ''))}" maxlength="16">
        ${dadosPessoaisErrors.telefone ? `<div class="field-error">${dadosPessoaisErrors.telefone}</div>` : ''}
      </div>
    `;
    }

    function renderEnderecoStep(): string {
        return `
      <div class="section-label">Informações de Endereço</div>
      <div class="field">
        <label>CEP</label>
        <input type="text" id="cep" placeholder="00000-000" value="${escapeHtmlAttr(String(formData.cep || ''))}" maxlength="9" ${cepLoading ? 'disabled' : ''}>
        ${cepLoading ? '<div class="cep-loading"><span class="spinner"></span>Buscando CEP...</div>' : ''}
        ${enderecoErrors.cep ? `<div class="field-error">${enderecoErrors.cep}</div>` : ''}
      </div>
      <div class="field">
        <label>Endereço</label>
        <input type="text" id="endereco" placeholder="Rua exemplo" value="${escapeHtmlAttr(String(formData.endereco || ''))}" maxlength="100">
        ${enderecoErrors.endereco ? `<div class="field-error">${enderecoErrors.endereco}</div>` : ''}
      </div>
      <div class="field">
        <label>Número</label>
        <input type="text" id="numero" placeholder="" value="${escapeHtmlAttr(String(formData.numero || ''))}" maxlength="8">
        ${enderecoErrors.numero ? `<div class="field-error">${enderecoErrors.numero}</div>` : ''}
      </div>
      <div class="field">
        <label>Complemento <small>(opcional)</small></label>
        <input type="text" id="complemento" placeholder="" value="${escapeHtmlAttr(String(formData.complemento || ''))}" maxlength="60">
        ${enderecoErrors.complemento ? `<div class="field-error">${enderecoErrors.complemento}</div>` : ''}
      </div>
      <div class="field">
        <label>Bairro</label>
        <input type="text" id="bairro" placeholder="" value="${escapeHtmlAttr(String(formData.bairro || ''))}" maxlength="80">
        ${enderecoErrors.bairro ? `<div class="field-error">${enderecoErrors.bairro}</div>` : ''}
      </div>
      <div class="field-group">
        <div class="field">
          <label>Estado</label>
          <select id="estado">
            <option value="">Selecione</option>
            ${UFS.map((u) => `<option value="${u.sigla}" ${formData.estado === u.sigla ? 'selected' : ''}>${u.nome}</option>`).join('')}
          </select>
          ${enderecoErrors.estado ? `<div class="field-error">${enderecoErrors.estado}</div>` : ''}
        </div>
        <div class="field">
          <label>Cidade</label>
          <select id="cidade">
            <option value="">Selecione</option>
            ${cidadesList.map((c) => `<option value="${escapeHtmlAttr(c.nome)}" ${formData.cidade === c.nome ? 'selected' : ''}>${escapeHtmlAttr(c.nome)}</option>`).join('')}
          </select>
          ${enderecoErrors.cidade ? `<div class="field-error">${enderecoErrors.cidade}</div>` : ''}
        </div>
      </div>
    `;
    }

    function renderCartaoMockStep(): string {
        const err = cardTokenizeErrorMessage
            ? `<div class="field-error decola-card-token-error" style="margin-bottom:0.75rem;line-height:1.4;">${escapeHtmlAttr(cardTokenizeErrorMessage)}</div>`
            : '';
        return `${err}
      <div class="section-label">Dados do cartão</div>
      ${enrollmentConfirmationNoteHtml(installmentsForSelectedPlanMock())}
      <div class="decola-card-mock">
        <div class="decola-card-fields">
          <div class="decola-card-row">
            <label for="card-holder">Nome no cartão</label>
            <input id="card-holder" type="text" autocomplete="cc-name" placeholder="Como está impresso no cartão" value="">
          </div>
          <div class="decola-card-row">
            <label for="card-number">Número do cartão</label>
            <input id="card-number" type="text" inputmode="numeric" placeholder="0000 0000 0000 0000" value="">
          </div>
          <div class="decola-card-expiry-grid">
            <div class="decola-card-row">
              <label for="card-mm">Mês</label>
              <input id="card-mm" maxlength="2" inputmode="numeric" placeholder="MM" value="">
            </div>
            <div class="decola-card-row">
              <label for="card-yy">Ano</label>
              <input id="card-yy" maxlength="4" inputmode="numeric" placeholder="AAAA" value="">
            </div>
            <div class="decola-card-row">
              <label for="card-cvv">CVV</label>
              <input id="card-cvv" maxlength="4" inputmode="numeric" placeholder="000" value="">
            </div>
          </div>
        </div>
      </div>`;
    }

    function renderCheckoutSuccess(): string {
        const confirmNotePixBoleto = enrollmentConfirmationNoteHtml(installmentsForSelectedPlanMock());
        if (inlinePaymentResult) {
            const ip = inlinePaymentResult as { type?: string; pix?: Record<string, string>; boleto?: Record<string, string>; creditCard?: Record<string, string> };
            const t = ip.type;
            if (t === 'pix' && ip.pix) {
                const px = ip.pix;
                const qrUrl = escapeHtmlAttr(px.qrCodeUrl || '');
                const qrText = escapeHtmlAttr(px.qrCode || '');
                const exp = escapeHtmlAttr(formatIsoDateTimeForDisplay(String(px.expiresAt || '')));
                return `
      <div class="checkout-success">
        <h3>PIX</h3>
        <p>Escaneie o QR Code ou copie o código para pagar no app do banco.</p>
        ${confirmNotePixBoleto}
        <div class="inline-payment-pix">
          ${qrUrl ? `<div class="qr-wrap"><img src="${qrUrl}" alt="QR Code PIX" width="220" height="220"></div>` : ''}
          <label style="display:block;font-size:0.8rem;margin-top:0.35rem;">Código copia e cola</label>
          <textarea readonly id="decola-pix-paste" rows="3" spellcheck="false">${qrText}</textarea>
          <button type="button" class="btn-copy" id="btn-copy-pix">Copiar código PIX</button>
          ${exp ? `<p style="font-size:0.8rem;margin-top:0.75rem;opacity:0.9;">Expira em: ${exp}</p>` : ''}
        </div>
      </div>`;
            }
            if (t === 'boleto' && ip.boleto) {
                const b = ip.boleto;
                const line = escapeHtmlAttr(b.line || '');
                const pdf = escapeHtmlAttr(b.pdfUrl || '');
                const due = escapeHtmlAttr(formatIsoDateTimeForDisplay(String(b.dueAt || '')));
                return `
      <div class="checkout-success">
        <h3>Boleto</h3>
        <p>Use a linha digitável abaixo ou o PDF para pagar.</p>
        ${confirmNote}
        <div class="inline-payment-boleto">
          <div class="boleto-line" id="decola-boleto-line">${line}</div>
          <button type="button" class="btn-copy" id="btn-copy-boleto">Copiar linha digitável</button>
          ${pdf ? `<a href="${pdf}" target="_blank" rel="noopener" class="pdf-link">Abrir boleto (PDF)</a>` : ''}
          ${due ? `<p style="font-size:0.8rem;margin-top:0.75rem;">Vencimento: ${due}</p>` : ''}
        </div>
      </div>`;
            }
            if (t === 'credit_card' && ip.creditCard) {
                const c = ip.creditCard;
                const st = escapeHtmlAttr(c.status || '');
                const msg = escapeHtmlAttr(c.message || '');
                const last = escapeHtmlAttr(c.lastFour || '');
                return `
      <div class="checkout-success">
        <h3>Cartão de crédito</h3>
        <div class="inline-payment-card">
          <p>Status: <strong>${st}</strong></p>
          ${last ? `<p>Final ${last}</p>` : ''}
          ${msg ? `<p style="font-size:0.85rem;">${msg}</p>` : ''}
        </div>
      </div>`;
            }
        }
        if (checkoutUrl) {
            return `
      <div class="checkout-success">
        <h3>Quase lá!</h3>
        <p>Sua inscrição foi registrada. Clique no botão abaixo para finalizar o pagamento.</p>
        <a href="${escapeHtmlAttr(checkoutUrl)}" class="btn-checkout" id="btn-checkout" target="_blank" rel="noopener">Ir para o checkout</a>
      </div>
    `;
        }
        return `
      <div class="checkout-success">
        <h3>Pagamento criado</h3>
        <p>Pedido registrado (simulação).</p>
      </div>`;
    }

    function loadCidades(uf: string): void {
        if (!uf) {
            cidadesList = [];
            return;
        }
        cidadesList = MOCK_CIDADES[uf] || [];
    }

    function mockFetchCep(cepRaw: string): void {
        const digits = String(cepRaw).replace(/\D/g, '');
        if (digits.length !== 8) return;
        collectStepData();
        formData.cep = maskCep(digits);
        cepLoading = true;
        render();
        const start = Date.now();
        window.setTimeout(() => {
            formData.endereco = 'Rua Demonstração, mock';
            formData.bairro = 'Centro';
            formData.estado = 'RS';
            formData.cidade = 'Porto Alegre';
            loadCidades('RS');
            cepLoading = false;
            const elapsed = Date.now() - start;
            const remaining = Math.max(0, 400 - elapsed);
            window.setTimeout(() => render(), remaining);
        }, 500);
    }

    async function runMockSubmit(): Promise<void> {
        submitting = true;
        checkoutErrorMessage = null;
        render();
        await new Promise((r) => setTimeout(r, 900));
        const fp = String(formData.formaPagamento);
        if (fp === 'pix') {
            inlinePaymentResult = {
                type: 'pix',
                pix: {
                    qrCodeUrl: 'https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg',
                    qrCode:
                        '00020126580014br.gov.bcb.pix0136mock-pix-copia-e-cola-demo52040000530398654041.005802BR5925DECOLA EDU MOCK6009SAO PAULO62070503***6304ABCD',
                    expiresAt: new Date(Date.now() + 86400000).toISOString(),
                },
            };
            checkoutUrl = null;
        } else if (fp === 'boleto') {
            inlinePaymentResult = {
                type: 'boleto',
                boleto: {
                    line: '34191.79001 01043.510047 91020.150008 1 93700026000',
                    pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
                    dueAt: new Date(Date.now() + 5 * 86400000).toISOString(),
                },
            };
            checkoutUrl = null;
        } else {
            checkoutUrl = 'https://example.com/checkout-mock';
            inlinePaymentResult = null;
        }
        submitting = false;
        if (apiBase) {
            const ok = await syncProgress('matricula');
            if (!ok) return;
        }
        render();
    }

    function validateCartaoMock(): boolean {
        const holder = (shadow.getElementById('card-holder') as HTMLInputElement | null)?.value?.trim() || '';
        const num = (shadow.getElementById('card-number') as HTMLInputElement | null)?.value?.replace(/\D/g, '') || '';
        const mm = (shadow.getElementById('card-mm') as HTMLInputElement | null)?.value?.trim() || '';
        const yy = (shadow.getElementById('card-yy') as HTMLInputElement | null)?.value?.trim() || '';
        const cvv = (shadow.getElementById('card-cvv') as HTMLInputElement | null)?.value?.trim() || '';
        if (!holder || holder.length < 3) {
            cardTokenizeErrorMessage = 'Informe o nome como está no cartão.';
            return false;
        }
        if (num.length < 13) {
            cardTokenizeErrorMessage = 'Número do cartão inválido.';
            return false;
        }
        if (!/^\d{1,2}$/.test(mm) || parseInt(mm, 10) < 1 || parseInt(mm, 10) > 12) {
            cardTokenizeErrorMessage = 'Mês de validade inválido.';
            return false;
        }
        if (!/^\d{4}$/.test(yy)) {
            cardTokenizeErrorMessage = 'Ano de validade inválido.';
            return false;
        }
        if (cvv.length < 3) {
            cardTokenizeErrorMessage = 'CVV inválido.';
            return false;
        }
        cardTokenizeErrorMessage = null;
        return true;
    }

    async function runMockCardPay(): Promise<void> {
        if (!validateCartaoMock()) {
            render();
            return;
        }
        submitting = true;
        render();
        await new Promise((r) => setTimeout(r, 800));
        const lastFour = String((shadow.getElementById('card-number') as HTMLInputElement)?.value || '').replace(/\D/g, '').slice(-4);
        inlinePaymentResult = {
            type: 'credit_card',
            creditCard: {
                status: 'Aprovado (mock)',
                message: 'Transação simulada com sucesso.',
                lastFour: lastFour || '4242',
            },
        };
        checkoutUrl = null;
        submitting = false;
        if (apiBase) {
            const ok = await syncProgress('matricula');
            if (!ok) return;
        }
        render();
    }

    function goBack(): void {
        collectStepData();
        checkoutErrorMessage = null;
        cardTokenizeErrorMessage = null;
        if (currentStep === STEPS.CARTAO) {
            currentStep = STEPS.ENDERECO;
        } else if (currentStep === STEPS.ENDERECO) {
            currentStep = STEPS.DADOS_PESSOAIS;
        } else if (currentStep === STEPS.DADOS_PESSOAIS) {
            currentStep = STEPS.PAGAMENTO;
        }
        render();
    }

    async function goNext(): Promise<void> {
        collectStepData();
        if (currentStep === STEPS.PAGAMENTO) {
            if (!getCurrentPlan()) {
                pagamentoError = true;
                render();
                return;
            }
            if (!formData.aceiteTermo) {
                termosError = true;
                render();
                return;
            }
            termosError = false;
            pagamentoError = false;
            if (!(await syncProgress('pagamento'))) return;
            currentStep = STEPS.DADOS_PESSOAIS;
            render();
            return;
        }
        if (currentStep === STEPS.DADOS_PESSOAIS) {
            dadosPessoaisErrors = validateDadosPessoais(formData);
            if (Object.keys(dadosPessoaisErrors).length > 0) {
                render();
                return;
            }
            dadosPessoaisErrors = {};
            if (!(await syncProgress('dados_basicos'))) return;
            currentStep = STEPS.ENDERECO;
            render();
            return;
        }
        if (currentStep === STEPS.ENDERECO) {
            enderecoErrors = validateEndereco(formData);
            if (Object.keys(enderecoErrors).length > 0) {
                render();
                return;
            }
            enderecoErrors = {};
            if (!(await syncProgress('dados_endereco'))) return;
            if (formData.formaPagamento === 'cartao') {
                currentStep = STEPS.CARTAO;
                render();
                return;
            }
            await runMockSubmit();
            return;
        }
    }

    let shadowDelegatedBound = false;

    function attachListeners(): void {
        shadow.querySelectorAll('.btn-tipo[data-tipo]').forEach((btn) => {
            btn.addEventListener('click', () => {
                collectStepData();
                pagamentoError = false;
                formData.tipoIngresso = (btn as HTMLButtonElement).dataset.tipo;
                const methods = plansByCustomerAndMethod[String(formData.tipoIngresso)] || {};
                const firstMethod = ['pix', 'cartao', 'boleto'].find((m) => methods[m]);
                if (firstMethod && !methods[String(formData.formaPagamento)]) formData.formaPagamento = firstMethod;
                syncPlanFromFormData();
                render();
            });
        });
        shadow.querySelectorAll('.payment-option[data-pag]').forEach((el) => {
            el.addEventListener('click', () => {
                collectStepData();
                pagamentoError = false;
                formData.formaPagamento = (el as HTMLElement).dataset.pag;
                syncPlanFromFormData();
                render();
            });
        });
        const cbCupom = shadow.getElementById('cupom');
        if (cbCupom) {
            cbCupom.addEventListener('change', () => {
                collectStepData();
                if (!(cbCupom as HTMLInputElement).checked) {
                    couponError = null;
                    formData.coupon_id = null;
                    formData.coupon_discount_type = null;
                    formData.coupon_amount = null;
                    formData.coupon_code_display = '';
                    formData.coupon_scholarship_name = '';
                    formData.codigoCupom = '';
                }
                pagamentoError = false;
                termosError = false;
                void render();
            });
        }
        const btnAplicarCupom = shadow.getElementById('btn-aplicar-cupom');
        if (btnAplicarCupom) {
            btnAplicarCupom.addEventListener('click', () => {
                void applyCouponFlow();
            });
        }
        const cbAceiteTermo = shadow.getElementById('aceiteTermo');
        if (cbAceiteTermo)
            cbAceiteTermo.addEventListener('change', () => {
                termosError = false;
                pagamentoError = false;
                collectStepData();
                render();
            });
        const cbNomeSocial = shadow.getElementById('possuiNomeSocial');
        if (cbNomeSocial) {
            cbNomeSocial.addEventListener('change', () => {
                collectStepData();
                formData.possuiNomeSocial = (cbNomeSocial as HTMLInputElement).checked;
                if (!(cbNomeSocial as HTMLInputElement).checked) formData.nomeSocial = '';
                render();
            });
        }
        const cbEstrangeiro = shadow.getElementById('estrangeiro');
        if (cbEstrangeiro) {
            cbEstrangeiro.addEventListener('change', () => {
                collectStepData();
                formData.estrangeiro = (cbEstrangeiro as HTMLInputElement).checked;
                if ((cbEstrangeiro as HTMLInputElement).checked) formData.cpf = '';
                else formData.passaporte = '';
                dadosPessoaisErrors = {};
                render();
            });
        }
        const inputCpf = shadow.getElementById('cpf');
        if (inputCpf) {
            inputCpf.addEventListener('input', () => {
                (inputCpf as HTMLInputElement).value = maskCpf((inputCpf as HTMLInputElement).value);
                delete dadosPessoaisErrors.cpf;
                const errEl = shadow.getElementById('cpf-error');
                if (errEl) {
                    errEl.textContent = '';
                    errEl.style.display = 'none';
                }
            });
            inputCpf.addEventListener('blur', () => {
                collectStepData();
                if (!formData.estrangeiro) {
                    const digits = String(formData.cpf || '').replace(/\D/g, '');
                    if (digits.length === 11) {
                        if (!isValidCpf(String(formData.cpf))) dadosPessoaisErrors.cpf = 'CPF inválido.';
                        else delete dadosPessoaisErrors.cpf;
                    } else if (digits.length > 0) {
                        dadosPessoaisErrors.cpf = 'CPF inválido. Informe os 11 dígitos.';
                    } else delete dadosPessoaisErrors.cpf;
                }
                render();
            });
        }
        const inputTelefone = shadow.getElementById('telefone');
        if (inputTelefone) {
            inputTelefone.addEventListener('input', () => {
                (inputTelefone as HTMLInputElement).value = maskTelefone((inputTelefone as HTMLInputElement).value);
            });
        }
        const inputCep = shadow.getElementById('cep');
        if (inputCep) {
            inputCep.addEventListener('input', () => {
                (inputCep as HTMLInputElement).value = maskCep((inputCep as HTMLInputElement).value);
                const digits = (inputCep as HTMLInputElement).value.replace(/\D/g, '');
                if (digits.length === 8 && !cepLoading) mockFetchCep((inputCep as HTMLInputElement).value);
            });
        }
        const selectEstado = shadow.getElementById('estado');
        if (selectEstado) {
            selectEstado.addEventListener('change', () => {
                collectStepData();
                formData.estado = (selectEstado as HTMLSelectElement).value;
                formData.cidade = '';
                loadCidades(String(formData.estado));
                render();
            });
            if (currentStep === STEPS.ENDERECO && formData.estado && cidadesList.length === 0) {
                loadCidades(String(formData.estado));
            }
        }
        if (!shadowDelegatedBound) {
            shadowDelegatedBound = true;
            let saveTimeout: ReturnType<typeof setTimeout> | null = null;
            shadow.addEventListener('input', () => {
                if (saveTimeout) clearTimeout(saveTimeout);
                saveTimeout = setTimeout(() => collectStepData(), 400);
            });
            shadow.addEventListener('change', () => collectStepData());
        }
    }

    function render(): void {
        if (checkoutUrl || inlinePaymentResult) {
            shadow.innerHTML = `
        <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
        <div class="widget widget-checkout-success" data-offer-id="${escapeHtmlAttr(offerId)}" data-theme-id="${escapeHtmlAttr(themeId)}" data-seller-id="${escapeHtmlAttr(sellerId)}" data-campaign-id="${escapeHtmlAttr(campaignId)}">
          <h2>GARANTA SUA VAGA</h2>
          <div id="step-content">${renderCheckoutSuccess()}</div>
        </div>
      `;
            const btnPix = shadow.getElementById('btn-copy-pix');
            if (btnPix) {
                btnPix.addEventListener('click', () => {
                    const ta = shadow.getElementById('decola-pix-paste') as HTMLTextAreaElement | null;
                    if (ta && navigator.clipboard) navigator.clipboard.writeText(ta.value).catch(() => {});
                });
            }
            const btnBol = shadow.getElementById('btn-copy-boleto');
            if (btnBol) {
                btnBol.addEventListener('click', () => {
                    const el = shadow.getElementById('decola-boleto-line');
                    if (el && navigator.clipboard) navigator.clipboard.writeText(el.textContent || '').catch(() => {});
                });
            }
            return;
        }

        const nextLabel = submitting
            ? 'Enviando...'
            : currentStep === STEPS.CARTAO
              ? 'Pagar'
              : currentStep === STEPS.ENDERECO
                ? 'Concluir'
                : 'Avançar';

        const widgetStepClass =
            currentStep === STEPS.PAGAMENTO ? 'step-pagamento' : currentStep === STEPS.CARTAO ? 'step-cartao' : '';

        shadow.innerHTML = `
      <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
      <div class="widget ${widgetStepClass}" data-offer-id="${escapeHtmlAttr(offerId)}" data-theme-id="${escapeHtmlAttr(themeId)}" data-seller-id="${escapeHtmlAttr(sellerId)}" data-campaign-id="${escapeHtmlAttr(campaignId)}">
        ${submitting ? '<div class="widget-submitting-overlay"><span class="spinner"></span><span>Enviando sua inscrição...</span></div>' : ''}
        <h2>GARANTA SUA VAGA</h2>
        ${checkoutErrorMessage ? `<div class="field-error" style="margin-bottom:1rem;line-height:1.4;">${escapeHtmlAttr(checkoutErrorMessage)}</div>` : ''}
        <div id="step-content"></div>
        <div class="actions">
          ${currentStep > firstStep && currentStep !== STEPS.CARTAO ? '<button type="button" class="secondary" id="btn-back">Voltar</button>' : ''}
          <button type="button" class="primary btn-next" id="btn-next" ${submitting ? 'disabled' : ''}>${nextLabel}</button>
        </div>
      </div>
    `;

        const stepContent = shadow.getElementById('step-content');
        if (!stepContent) return;

        if (currentStep === STEPS.PAGAMENTO) {
            stepContent.innerHTML = renderPagamentoStep();
            syncPlanFromFormData();
        } else if (currentStep === STEPS.DADOS_PESSOAIS) {
            stepContent.innerHTML = renderDadosPessoaisStep();
        } else if (currentStep === STEPS.ENDERECO) {
            stepContent.innerHTML = renderEnderecoStep();
        } else if (currentStep === STEPS.CARTAO) {
            stepContent.innerHTML = renderCartaoMockStep();
        }

        const btnNext = shadow.getElementById('btn-next');
        if (btnNext) {
            btnNext.addEventListener('click', () => {
                if (currentStep === STEPS.CARTAO) void runMockCardPay();
                else void goNext();
            });
        }
        const btnBack = shadow.getElementById('btn-back');
        if (btnBack) btnBack.addEventListener('click', () => goBack());

        attachListeners();
    }

    render();
}
